// src/components/gavion/integrations/microsoftHandoff.ts
// After Microsoft sign-in, Supabase exposes provider_token/provider_refresh_token once in the session
// (spec 01 §2.1). Hand them to gavion-integration-connect/microsoft/store exactly once per refresh token.
export type HandoffStore = (t: {
  providerToken: string;
  providerRefreshToken: string;
}) => Promise<unknown>;
export type AuthSessionLike =
  | { provider_token?: string | null; provider_refresh_token?: string | null }
  | null
  | undefined;
type StorageLike = Pick<Storage, "getItem" | "setItem">;
const KEY = "gavion.ms_handoff";

async function fingerprint(token: string): Promise<string> {
  const d = new Uint8Array(
    await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token)),
  );
  return Array.from(d.slice(0, 12), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
}
const safeSessionStorage = (): StorageLike | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const TOKEN_HASH =
  /(provider_token|provider_refresh_token|access_token|refresh_token)=/;
/** Removes OAuth tokens from the URL hash (keeps router hashes such as #/companies). */
export function scrubAuthHash(): void {
  try {
    if (!TOKEN_HASH.test(window.location.hash)) return;
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  } catch {
    // ignore
  }
}

export async function maybeHandOff(
  event: string,
  session: AuthSessionLike,
  store: HandoffStore,
  storage: StorageLike | null,
): Promise<"stored" | "skipped" | "failed"> {
  scrubAuthHash();
  if (event !== "SIGNED_IN" && event !== "INITIAL_SESSION") return "skipped";
  const at = session?.provider_token;
  const rt = session?.provider_refresh_token;
  if (!at || !rt) return "skipped";
  let fp: string;
  try {
    fp = await fingerprint(rt);
  } catch {
    return "failed";
  }
  try {
    if (storage?.getItem(KEY) === fp) return "skipped";
  } catch {
    // storage unavailable: fall through and store (the server upsert is idempotent)
  }
  try {
    await store({ providerToken: at, providerRefreshToken: rt });
  } catch {
    return "failed"; // Settings shows "Connecter Microsoft 365" (status none)
  }
  try {
    storage?.setItem(KEY, fp);
  } catch {
    // ignore
  }
  return "stored";
}

type AuthClientLike = {
  auth: {
    onAuthStateChange(cb: (e: string, s: unknown) => void): {
      data: { subscription: { unsubscribe(): void } };
    };
  };
};
export function installMicrosoftHandoff(
  client: AuthClientLike,
  store: HandoffStore,
  storage: StorageLike | null = safeSessionStorage(),
): () => void {
  scrubAuthHash();
  const { data } = client.auth.onAuthStateChange((event, session) => {
    void maybeHandOff(event, session as AuthSessionLike, store, storage);
  });
  return () => data.subscription.unsubscribe();
}
